import { getId } from '@cawita/core-front';
import { CrudService, RequestConfig } from '@cawita/core-front/api';
import { AsyncStore, StoreOptions } from '@cawita/core-front/state';
import { EMPTY, Observable } from 'rxjs';
import { distinctUntilChanged, map, pluck, tap } from 'rxjs/operators';

export interface DefaultRessourceState<D> {
    item: D;
    new: boolean;
}

export interface DefaultSaveResponse<D> {
    item: D;
    isCreation: boolean;
}

export interface DefaultRessourceStoreOptions extends StoreOptions {
    customOptions?: Record<string, string | number>;
}

export class DefaultRessourceStore<D> extends AsyncStore<DefaultRessourceState<D>, string | D> {

    item$: Observable<D> = this.state$.pipe(pluck('item'), distinctUntilChanged());
    loading$: Observable<boolean> = this.state$.pipe(pluck('loading'));

    constructor(
        protected api: CrudService<D>,
        protected options?: DefaultRessourceStoreOptions,
    ) {
        super({
            item: null,
            new: false,
            initialized: false,
            loading: false
        }, options);
    }

    protected getProjection(): string { return undefined; }
    protected getBypassCache(): boolean { return false; }

    public init(itemId?: string) {
        if (itemId === 'new') itemId = null;
        return this._getData(itemId);
    }

    public refresh() {
        const { item } = this.getValue();
        return this._getData(getId(item), {
            bypassCache: true
        });
    }

    protected requestOptions(conf?: RequestConfig): RequestConfig {
        return {
            ...(conf || {}),
            options: {
                ...(conf?.options || {}),
                projection: this.getProjection(),
                ...(this.options?.customOptions ?? {})
            }
        }
    }

    private _getData(itemId?: string, options?: RequestConfig): Observable<D> {
        this.setState((s) => ({ ...s, new: !itemId }));
        if (!!itemId) {
            this.setLoading(true);
            return this.api.getOne(itemId, this.requestOptions(options)).pipe(
                tap(item => this.setItem(item)),
                tap(() => this.setLoading(false)),
                tap(() => this.setInitialized(true))
            );
        }
        this.setLoading(false);
        this.setInitialized(true);
        this.setState(s => ({ ...s, item: null }));
        return EMPTY;
    }

    public save(payload: Partial<D>): Observable<DefaultSaveResponse<D>> {
        const { item } = this.getValue();
        this.setLoading(true);

        let source: Observable<D>;
        if (!!item) source = this.api.update(getId(item), payload, this.requestOptions());
        else source = this.api.create(payload, this.requestOptions());

        return source.pipe(
            tap(newItem => this.setItem(newItem)),
            tap(() => this.setLoading(false)),
            map(newItem => ({ item: newItem, isCreation: !item })),
        );
    }

    public delete(): Observable<any> {
        const { item } = this.getValue();
        if (!item) return EMPTY;
        this.setLoading(true);
        return this.api.delete(getId(item)).pipe(
            tap(() => this.setItem(null)),
        );
    }

    protected setItem(item: D) {
        this.setState((s) => ({ ...s, new: !item, item }));
    }
}
